import React from "react";
import { motion } from "framer-motion";

/**
 * Unified animated "door" module — covers BOTH shapes used across PDAnimation:
 *
 *  shape="square" (default) — PurpleDoor / LifeReplays / Pulse / DigiBoutique
 *    FRAME -> ROOM (revealed behind door) -> animated DOOR -> HANDLE -> GLOW
 *    Responsive: w-16 h-24 (<360px) / w-20 h-28 (>=360px) / w-24 h-32 (>=640px)
 *
 *  shape="pill" — Hustle / FindItLocal / Nabora / FoundHer / VocalForLocal
 *    FRAME -> animated DOOR (flat/gradient bg, no separate room layer) -> HANDLE -> GLOW
 *    Responsive: w-40 h-10 (<360px) / w-44 h-11 (>=360px) / w-48 h-12 (>=640px)
 */
export default function DoorModule({
  open,
  doorStatus,
  authState,
  onEnter,
  onDoorClick,
  soundSrc,
  soundVolume = 0.8,
  clickDelay = 50,
  shape = "square",
  sizeClass,
  frameClass = "border-purple-500",
  frameExtraClass = "",
  roomGradientClass = "from-purple-900 via-purple-950 to-black",
  roomTextClass = "text-green-300",
  roomLabel,
  doorBgClass = "bg-gradient-to-b from-purple-500 via-purple-700 to-purple-900",
  doorIcon,
  doorLabel,
  pillLine1,
  pillLine2,
  handleClass = "bg-white",
  glowClass = "bg-purple-400",
  glowVariant = "single",
  welcomeIcon = "🚀",
  welcomeSubLabel,
}) {
  const isPill = shape === "pill";

  // const defaultSize = isPill
  //   ? "w-40 h-10 xs:w-44 xs:h-11 sm:w-48 sm:h-12"
  //   : "w-16 h-24 xs:w-20 xs:h-28 sm:w-24 sm:h-32";

  const defaultSize = isPill
    ? "w-40 h-5 xs:w-44 xs:h-5.5 sm:w-48 sm:h-6"
    : "w-16 h-12 xs:w-20 xs:h-14 sm:w-24 sm:h-16";

  const playDoorSound = () => {
    if (!soundSrc) return;
    const audio = new Audio(soundSrc);
    audio.volume = soundVolume;
    audio.play().catch(() => {});
  };

  const handleClick = () => {
    playDoorSound();
    setTimeout(() => {
      if (onDoorClick) onDoorClick();
      onEnter();
    }, clickDelay);
  };

  return (
    <div className={`flex justify-center items-center ${isPill ? "mt-3" : "mt-2"}`}>
      <div
        className={`relative ${sizeClass || defaultSize} cursor-pointer overflow-hidden shrink-0`}
        onClick={handleClick}
      >
        <div
          className={`absolute inset-0 border-4 ${frameClass} ${
            isPill ? "rounded-lg" : "rounded-md"
          } ${frameExtraClass}`}
        />

        {!isPill && (
          <div
            className={`absolute inset-1 flex items-center justify-center text-center bg-gradient-to-b ${roomGradientClass} rounded-sm px-1`}
          >
            <div
              className={`${roomTextClass} text-[8px] xs:text-[9px] sm:text-[10px] font-semibold leading-tight`}
            >
              {roomLabel}
            </div>
          </div>
        )}

        <motion.div
          className={`absolute inset-1 ${
            isPill ? "rounded-md px-2" : "rounded-sm px-1"
          } origin-left z-10 flex items-center justify-center ${doorBgClass}`}
          animate={{ rotateY: open ? -95 : 0 }}
          transition={{ duration: isPill ? 0.75 : 0.7, ease: "easeInOut" }}
          style={{ transformStyle: "preserve-3d" }}
        >
          <div
            className={`flex flex-col text-center text-white leading-tight ${
              isPill ? "text-[10px] font-semibold" : "text-[8px] xs:text-[9px] font-semibold"
            }`}
          >
            {doorStatus === "idle" && !isPill && (
              <div className="flex flex-col justify-center">
                <p className="text-xs sm:text-sm lg:text-lg">{doorIcon}</p>
                <p className="text-[9px] xs:text-[11px] lg:text-xs text-white">
                  {doorLabel}
                </p>
              </div>
            )}

            {doorStatus === "idle" && isPill && (
              <div className="flex flex-col justify-center">
                <p className="text-[11px] lg:text-[12px] text-white">{pillLine1}</p>
                <p className="text-[9px] lg:text-[10px] text-white">{pillLine2}</p>
              </div>
            )}

            {doorStatus === "welcome" && !isPill && (
              <>
                {welcomeIcon} Welcome
                <br />
                {authState?.user?.name}
              </>
            )}

            {doorStatus === "welcome" && isPill && (
              <div className="flex flex-col items-center">
                <p className="text-cyan-300">{welcomeIcon} Welcome</p>
                <p className="text-violet-100 text-[10px]">{authState?.user?.name}</p>
                {welcomeSubLabel && (
                  <p className="text-[8px] text-pink-300">{welcomeSubLabel}</p>
                )}
              </div>
            )}
          </div>

          <div
            className={`absolute ${
              isPill ? "right-2 top-1/2 w-2 h-2" : "right-1.5 top-2/3 w-1.5 h-1.5"
            } -translate-y-1/2 rounded-full ${handleClass}`}
          />
        </motion.div>

        {glowVariant === "single" && (
          <motion.div
            className={`absolute inset-0 ${glowClass} blur-md z-0`}
            animate={{ opacity: open ? 0.6 : 0.15 }}
          />
        )}
        {glowVariant === "double" && (
          <>
            <motion.div
              className={`absolute inset-0 ${glowClass} blur-xl z-0`}
              animate={{ opacity: open ? 0.7 : 0.2 }}
            />
            <motion.div
              className="absolute inset-0 border border-cyan-400/20 rounded-lg z-0"
              animate={{ opacity: open ? 0.6 : 0.15 }}
            />
          </>
        )}
      </div>
    </div>
  );
}